import React, { FunctionComponent } from 'react';

import { connect, useDispatch } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';

import TabsNav, { TabProp } from '@components/tabs-nav';
import { push } from 'connected-react-router';
import styled from 'styled-components';

interface MerchMenuMobileProps {
  className?: string;
  isMobile: boolean;
  isEdit?: boolean;
  productSelectionDone: boolean;
  colorSizeSelectionDone: boolean;
  previewDone: boolean;
}

type MerchTabs = 'select-product' | 'color-size' | 'design' | 'pricing-owners';

const initialTab = 0;

const mapStateToProps = (state: AppState) => {
  const productId =
    state.merch.active?.editProduct?.id ||
    state.merch.active?.newProduct?.productId;

  return {
    isMobile: state.ui.mobile,
    productSelectionDone:
      !!state.merch.active?.product || !!state.merch.active?.editProduct,
    colorSizeSelectionDone:
      !!state.merch.active?.selectedColors &&
      !!state.merch.active?.selectedColors.length &&
      !!state.merch.active?.selectedSizes &&
      !!state.merch.active?.selectedSizes?.length,
    previewDone: !!productId && !!state.merch?.active?.productData,
  };
};

const MerchMenuMobile: FunctionComponent<MerchMenuMobileProps> = ({
  className,
  isEdit = false,
  productSelectionDone,
  colorSizeSelectionDone,
  previewDone,
}) => {
  const match = useRouteMatch<{ merchId: string }>();
  const location = useLocation();
  const merchId = match.params.merchId;
  const baseUrl = isEdit ? `/merch/edit/${merchId}` : '/merch';

  const tabsData: TabProp<MerchTabs>[] = React.useMemo(
    () => [
      {
        label: <Translate id="MERCH.SECTIONS.PRODUCT" />,
        key: 'select-product' as MerchTabs,
        disabled: false,
      },
      {
        label: <Translate id="MERCH.SECTIONS.COLOR" />,
        key: 'color-size' as MerchTabs,
        disabled: !productSelectionDone,
      },
      {
        label: <Translate id="MERCH.SECTIONS.DESIGN" />,
        key: 'design' as MerchTabs,
        disabled: !colorSizeSelectionDone,
      },
      {
        label: <Translate id="MERCH.SECTIONS.PRICING" />,
        key: 'pricing-owners' as MerchTabs,
        disabled: !previewDone,
      },
    ],
    [productSelectionDone, colorSizeSelectionDone, previewDone]
  );

  const [activeTab, setActiveTab] = React.useState<MerchTabs>(
    tabsData[initialTab].key
  );

  const getActiveTab = (url: string) => {
    const urlArray = url.split('/');
    const lastUrl = urlArray[urlArray.length - 1];
    return lastUrl;
  };

  React.useEffect(() => {
    if (getActiveTab(location.pathname) === 'select-product') {
      setActiveTab(tabsData[0].key);
    } else if (getActiveTab(location.pathname) === 'color-size') {
      setActiveTab(tabsData[1].key);
    } else if (getActiveTab(location.pathname) === 'design') {
      setActiveTab(tabsData[2].key);
    } else if (getActiveTab(location.pathname) === 'pricing-owners') {
      setActiveTab(tabsData[3].key);
    } else {
      setActiveTab(tabsData[0].key);
    }
  }, [tabsData, location.pathname]);

  const dispatch = useDispatch();

  const handleTabClick = (value: MerchTabs) => {
    setActiveTab(value);
    dispatch(push(`${baseUrl}/${value}`));
  };

  return (
    <TabsNav
      className={className || ''}
      tabs={tabsData}
      currentTab={tabsData.findIndex((tab) => tab.key === activeTab)}
      onChange={(tab) => handleTabClick(tab.key as MerchTabs)}
      bigFont
      activeBigFont
      overflow
    />
  );
};

export default connect(mapStateToProps)(styled(MerchMenuMobile)`
  padding-inline: 24px;
`);
